import { MessagesSquare, UserRoundCheck, UsersRound } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/ui/empty-state';
import { TextareaField } from '@/components/ui/field';
import { StatusBadge } from '@/components/ui/status-badge';
import { ConvertLeadDialog } from '@/pages/admin/leads/ConvertLeadDialog';
import { ConversationPane } from '@/pages/admin/conversations/ConversationPane';
import { CorrespondencePanel } from '@/pages/admin/conversations/CorrespondencePanel';
import {
  LEAD_TONES,
  useAssignLead,
  useDismissLeadDuplicate,
  useLead,
  useLeadAssignees,
  useSetLeadNotes,
  useSetLeadStatus,
} from '@/hooks/leads';
import { ApiError } from '@/lib/api';
import { formatInstant } from '@/lib/dates';
import { useToast } from '@/lib/toast-context';
import { cn } from '@/lib/utils';
import { LEAD_STATUS_TRANSITIONS, type LeadDetail } from '@shared/schemas/lead';

/*
 * The right-hand pane of the leads inbox — ROADMAP.md §9.8.
 *
 * **The property block is the reason this pane exists.** A quote request carries
 * bedrooms, bathrooms and a square-footage band for every property the customer
 * described, up to twenty, and that is the entire basis of the price the office
 * then writes. A detail pane showing a name and an email would be a contact form
 * wearing a quote form's title.
 */

export function LeadDetailPane({ leadId, onClose }: { leadId: string; onClose: () => void }) {
  const lead = useLead(leadId);
  const [converting, setConverting] = useState(false);

  if (lead.isError) {
    return (
      <div className="rounded-lg border border-ink-300 bg-white shadow-card">
        <ErrorState
          title="That lead could not be loaded"
          onRetry={() => {
            void lead.refetch();
          }}
        />
      </div>
    );
  }

  if (lead.isPending) {
    return (
      <div aria-busy="true" className="space-y-3">
        <span className="sr-only">Loading the lead</span>
        <div aria-hidden="true" className="h-96 animate-pulse rounded-lg bg-white" />
      </div>
    );
  }

  const detail = lead.data;

  return (
    <div className="space-y-4">
      <section className="rounded-lg border border-ink-300 bg-white p-4 shadow-card">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h2 className="text-h2 text-ink-900">{detail.businessName ?? detail.displayName}</h2>
            {detail.businessName ? (
              <p className="text-body text-ink-700">{detail.displayName}</p>
            ) : null}
            <p className="mt-1 flex flex-wrap items-center gap-2">
              <StatusBadge label={detail.statusLabel} tone={LEAD_TONES[detail.status]} />
              <span className="text-meta text-ink-500">
                {detail.sourceLabel} · {formatInstant(detail.createdAt)}
              </span>
            </p>
          </div>

          <Actions
            detail={detail}
            onConvert={() => {
              setConverting(true);
            }}
          />
        </div>

        <dl className="mt-4 grid gap-x-6 gap-y-2 text-body text-ink-700 sm:grid-cols-2">
          <Fact label="Email">
            {/* Null for an enquiry somebody rang in — migration 030. A
                `mailto:null` link is a broken link, and rendering it would be
                the same class of mistake as a KPI showing a dash for zero. */}
            {detail.email ? (
              <a
                href={`mailto:${detail.email}`}
                className="break-all text-bark-700 underline underline-offset-2"
              >
                {detail.email}
              </a>
            ) : (
              <Absent>Not given</Absent>
            )}
          </Fact>
          <Fact label="Phone">
            {detail.phone ? (
              <a
                href={`tel:${detail.phone}`}
                className="text-bark-700 underline underline-offset-2"
              >
                {detail.phone}
              </a>
            ) : (
              <Absent>Not given</Absent>
            )}
          </Fact>
          <Fact label="Where">
            {/* City, state and zip are what tell the office whether this is
                inside the service area — open question 7. */}
            {[detail.city, [detail.state, detail.postalCode].filter(Boolean).join(' ')]
              .filter((part) => part !== null && part !== '')
              .join(', ') || <Absent>Not given</Absent>}
          </Fact>
          <Fact label="Assigned to">
            <Assignment detail={detail} />
          </Fact>
        </dl>

        {detail.convertedClientId ? (
          <p className="mt-4 flex flex-wrap items-center gap-2 rounded-sm border border-ink-300 bg-honey-50 px-3 py-2.5 text-meta text-bark-900">
            <UserRoundCheck aria-hidden="true" className="size-4 shrink-0" />
            Became a client
            {detail.convertedAt ? ` on ${formatInstant(detail.convertedAt)}` : ''} —{' '}
            <Link
              to={`/admin/clients/${detail.convertedClientId}`}
              className="text-bark-700 underline underline-offset-2"
            >
              {detail.convertedClientName ?? 'open their record'}
            </Link>
          </p>
        ) : null}
      </section>

      <PossibleDuplicates detail={detail} />

      <Properties detail={detail} />

      <Notes key={detail.notes ?? ''} detail={detail} />

      {/*
        **This was a dashed panel saying replying would arrive with 9A**, and
        the statement of absence was deliberate: `lead_message` is struck in
        data-model.md §7, and a temporary second thread model would have been
        the exact mistake it was struck for. 9.7 replaces it with the real
        thing.

        The same panel appears on the client record, because after conversion it
        is the same thread — `lead_id` is kept and `client_id` is added, so the
        history reads continuously across the moment somebody clicked Convert.
      */}
      <section>
        <h3 className="mb-2 flex items-center gap-2 text-meta font-medium text-ink-900">
          <MessagesSquare aria-hidden="true" className="size-4 text-bark-700" />
          Correspondence
        </h3>
        {/*
          **Their message went onto somebody else's thread**, which happens when
          the address matched an existing customer exactly (migration 037). The
          panel below finds threads by `lead_id` and would show "No messages
          yet" about an enquiry that plainly had one — a screen confidently
          reporting that nothing happened, which is P22 wearing a new hat. So
          this reads the thread the message is actually on and says why.
        */}
        {detail.correspondenceConversationId === null ? (
          <CorrespondencePanel
            leadId={detail.id}
            contactName={detail.displayName}
            contactEmail={detail.email}
            emptyHint="Nothing has been sent to them yet. Their enquiry came in through the website form."
          />
        ) : (
          <div className="space-y-2">
            <p className="rounded-sm border border-ink-300 bg-honey-50 px-3 py-2.5 text-meta text-bark-900">
              This enquiry came from somebody we already had, so their message was added to their
              existing conversation rather than starting a second one.
            </p>
            <ConversationPane id={detail.correspondenceConversationId} compact />
          </div>
        )}
      </section>

      <ConvertLeadDialog
        detail={detail}
        open={converting}
        onOpenChange={setConverting}
        onConverted={onClose}
      />
    </div>
  );
}

/**
 * The per-state actions.
 *
 * **`Convert` is a separate control from the status buttons**, because it
 * creates a customer record. The status enum has no `converted` in it at all —
 * a lead marked converted with no client behind it is a lie the office finds out
 * about later.
 */
function Actions({ detail, onConvert }: { detail: LeadDetail; onConvert: () => void }) {
  const toast = useToast();
  const setStatus = useSetLeadStatus(detail.id);
  const allowed = LEAD_STATUS_TRANSITIONS[detail.status];

  if (detail.status === 'converted') {
    return (
      <Button asChild variant="outline" className="shrink-0">
        <Link to={`/admin/clients/${detail.convertedClientId ?? ''}`}>Open client</Link>
      </Button>
    );
  }

  function move(status: 'new' | 'contacted' | 'qualified' | 'lost', done: string) {
    setStatus.mutate(
      { status },
      {
        onSuccess: () => {
          toast.success(done);
        },
        onError: (cause) => {
          toast.error(cause instanceof ApiError ? cause.message : 'That could not be saved.');
        },
      },
    );
  }

  return (
    <div className="flex shrink-0 flex-wrap gap-2">
      {allowed.includes('contacted') ? (
        <Button
          variant="outline"
          disabled={setStatus.isPending}
          onClick={() => {
            move('contacted', 'Marked as contacted.');
          }}
        >
          Contacted
        </Button>
      ) : null}

      {allowed.includes('qualified') ? (
        <Button
          variant="outline"
          disabled={setStatus.isPending}
          onClick={() => {
            move('qualified', 'Marked as qualified.');
          }}
        >
          Qualified
        </Button>
      ) : null}

      {allowed.includes('lost') ? (
        <Button
          variant="ghost"
          disabled={setStatus.isPending}
          onClick={() => {
            move('lost', 'Marked as lost.');
          }}
        >
          Lost
        </Button>
      ) : null}

      {/* A lost lead that rings back on Monday goes straight back in. Working a
          lead is not a ratchet. */}
      {detail.status === 'lost' ? (
        <Button
          variant="outline"
          disabled={setStatus.isPending}
          onClick={() => {
            move('new', 'Back in the inbox.');
          }}
        >
          Reopen
        </Button>
      ) : null}

      <Button onClick={onConvert}>Convert</Button>
    </div>
  );
}

function Assignment({ detail }: { detail: LeadDetail }) {
  const toast = useToast();
  const assignees = useLeadAssignees();
  const assign = useAssignLead(detail.id);

  return (
    <label className="flex items-center gap-2">
      <span className="sr-only">Assign this lead</span>
      <select
        name="assignedToUserId"
        value={detail.assignedToUserId ?? ''}
        disabled={assign.isPending}
        onChange={(event) => {
          const value = event.target.value;
          assign.mutate(
            { assignedToUserId: value === '' ? null : value },
            {
              onSuccess: (saved) => {
                toast.success(
                  saved.assignedToName === null
                    ? 'Unassigned.'
                    : `Assigned to ${saved.assignedToName}.`,
                );
              },
              onError: (cause) => {
                toast.error(cause instanceof ApiError ? cause.message : 'That could not be saved.');
              },
            },
          );
        }}
        className="min-h-control rounded-sm border border-ink-300 bg-white px-2 text-body"
      >
        <option value="">Nobody</option>
        {(assignees.data?.assignees ?? []).map((entry) => (
          <option key={entry.id} value={entry.id}>
            {entry.name}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * Who this enquiry might already be — owner instruction, 2026-08-07.
 *
 * > *"An exact email match attaches to the existing person. A near-match on
 * > name and phone gets flagged for me — never auto-merged."*
 *
 * **Nothing on this panel merges anything, and there is no button that could.**
 * That is his rule rather than an omission: merging two customers who turn out
 * to be different people is very hard to undo and fails silently. The two
 * honest moves are already elsewhere on the screen — open the customer's record
 * and work the enquiry there, or convert this lead and keep both records.
 *
 * **Dismissing is the only action**, and it means "this is not the same
 * somebody". A flag that cannot be cleared is one that nags for ever about a
 * decision already made.
 *
 * A resolved row is shown too, greyed, because it is the answer to "why is
 * their message not on this enquiry" — see the correspondence section.
 */
function PossibleDuplicates({ detail }: { detail: LeadDetail }) {
  const toast = useToast();
  const dismiss = useDismissLeadDuplicate(detail.id);

  const shown = detail.duplicates.filter((entry) => entry.status !== 'dismissed');
  if (shown.length === 0) return null;

  const needsReview = shown.some((entry) => entry.status === 'open');

  return (
    <section
      className={cn(
        'rounded-lg border p-4 shadow-card',
        needsReview ? 'border-bark-500 bg-honey-50' : 'border-ink-300 bg-white',
      )}
    >
      <h3 className="flex items-center gap-2 text-h3 text-ink-900">
        <UsersRound aria-hidden="true" className="size-4 shrink-0 text-bark-700" />
        {needsReview ? 'This might be somebody you already have' : 'Already on record'}
      </h3>

      {needsReview ? (
        <p className="mt-1 text-meta text-ink-700">
          Nothing has been merged or linked. Have a look and decide.
        </p>
      ) : null}

      <ul className="mt-3 flex flex-col gap-2">
        {shown.map((entry) => (
          <li
            key={entry.id}
            className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-sm border border-ink-300 bg-white p-3"
          >
            <div className="min-w-0 flex-1">
              <p className="text-body text-ink-900">
                <Link
                  to={entry.candidateHref}
                  className="text-bark-700 underline underline-offset-2"
                >
                  {entry.candidateName}
                </Link>
                {entry.candidateIsArchived ? (
                  <span className="ml-2 text-meta text-ink-500">(archived)</span>
                ) : null}
              </p>
              {/* The sentence was written when the match was made — never the
                  stored `name_and_phone`. CLAUDE.md §4. */}
              <p className="text-meta text-ink-500">{entry.reason}</p>
            </div>

            {entry.status === 'open' ? (
              <Button
                variant="ghost"
                disabled={dismiss.isPending}
                onClick={() => {
                  dismiss.mutate(entry.id, {
                    onSuccess: () => {
                      toast.success('Noted — they are different people.');
                    },
                    onError: (cause) => {
                      toast.error(
                        cause instanceof ApiError ? cause.message : 'That could not be saved.',
                      );
                    },
                  });
                }}
              >
                Not the same person
              </Button>
            ) : (
              <span className="text-meta text-ink-500">Their message went here</span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * What the customer described.
 *
 * **Bathrooms come through as a string and the square footage as a band.** A
 * half bathroom is real and a rounded one is a wrong quote; the band is what the
 * customer chose from, and a midpoint would put a number they never said into a
 * price.
 */
function Properties({ detail }: { detail: LeadDetail }) {
  if (detail.properties.length === 0) {
    return (
      <section className="rounded-lg border border-ink-300 bg-white p-4 shadow-card">
        <h3 className="text-h3 text-ink-900">Their properties</h3>
        <p className="mt-2 text-body text-ink-700">
          {detail.source === 'contact_form'
            ? 'They sent a message rather than a quote request, so there are no property details.'
            : 'No property details came with this one.'}
        </p>
        {detail.notes ? null : (
          <p className="mt-1 text-meta text-ink-500">Ring them to find out what they need.</p>
        )}
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-ink-300 bg-white p-4 shadow-card">
      <h3 className="text-h3 text-ink-900">
        Their properties
        <span className="ml-2 rounded-full bg-ink-100 px-2 py-0.5 text-meta text-ink-700">
          {detail.properties.length}
        </span>
      </h3>

      <ul className="mt-3 grid gap-2 sm:grid-cols-2">
        {detail.properties.map((property, index) => (
          <li key={property.id} className="rounded-sm border border-ink-300 p-3">
            {/* "Property 1" is sort order 0 — the form's own numbering. */}
            <p className="text-meta text-ink-500">Property {index + 1}</p>
            <p className="text-body text-ink-900">
              {property.bedrooms} bed · {property.bathroomsLabel} bath
            </p>
            <p className="text-body text-ink-700">
              {/* The band the customer chose, never a midpoint. */}
              {property.squareFeetBandLabel} sq ft
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** The office's notes. On a quote request this is where the customer's own note landed. */
function Notes({ detail }: { detail: LeadDetail }) {
  const toast = useToast();
  const [notes, setNotes] = useState(detail.notes ?? '');
  const save = useSetLeadNotes(detail.id);
  const dirty = notes !== (detail.notes ?? '');

  return (
    <section className="space-y-3 rounded-lg border border-ink-300 bg-white p-4 shadow-card">
      <h3 className="text-h3 text-ink-900">Notes</h3>
      <TextareaField
        label="Notes on this enquiry"
        name="notes"
        rows={4}
        value={notes}
        onChange={(event) => {
          setNotes(event.target.value);
        }}
        hint="What they asked for, and anything said on the phone. Only the office sees this."
      />
      <div className="flex justify-end">
        <Button
          disabled={!dirty || save.isPending}
          onClick={() => {
            save.mutate(
              { notes },
              {
                onSuccess: () => {
                  toast.success('Notes saved.');
                },
                onError: (cause) => {
                  toast.error(
                    cause instanceof ApiError ? cause.message : 'That could not be saved.',
                  );
                },
              },
            );
          }}
        >
          {save.isPending ? 'Saving…' : 'Save notes'}
        </Button>
      </div>
    </section>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-x-2">
      <dt className="text-ink-500">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </div>
  );
}

function Absent({ children }: { children: React.ReactNode }) {
  return <span className="italic text-ink-500">{children}</span>;
}
